import {
  WeeklyBreakdownLeaderboard,
  DailyGameBreakdown,
  WeekDayPicker
} from "./-components"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { useMemo } from "react"

export const Route = createFileRoute("/(app)/app/results/daily")({
  component: DailyResultsPage,
})

const ALL_DIVISIONS = [
  "A",
  "B1",
  "B2",
  "C1",
  "C2",
  "C3",
  "D1",
  "D2",
  "D3",
  "D4",
]

function DailyResultsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  const selectedDivision = search.division
  const selectedGame = search.game

  const currentQuarter = useQuery(api.quarters.handlers.getCurrentQuarter)

  const weeklyBreakdownData = useQuery(
    api.quizzes.handlers.getDailyLeaderboardWithWeeklySummaries,
    currentQuarter
      ? {
          quarterId: currentQuarter._id,
          division: selectedDivision,
        }
      : "skip"
  )

  const dailyQuizzes = useQuery(
    api.quizzes.handlers.getPublishedDailyQuizzes,
    currentQuarter
      ? {
          quarterId: currentQuarter._id,
        }
      : "skip"
  )

  const gameBreakdown = useQuery(
    api.quizzes.handlers.getDailyGameBreakdown,
    selectedGame
      ? {
          quizId: selectedGame as Id<"quizzes">,
          division: selectedDivision,
        }
      : "skip"
  )

  const handleDivisionChange = (division: string) => {
    void navigate({
      to: "/app/results/daily",
      search: {
        ...search,
        division: division === "all" ? undefined : division,
        game: undefined,
      },
    })
  }

  const handleGameChange = (game: string) => {
    void navigate({
      to: "/app/results/daily",
      search: {
        ...search,
        game: game === "overall" ? undefined : game,
      },
    })
  }

  // Compute top-8-per-division qualifiers from weekly standings
  // Must be before early returns to satisfy Rules of Hooks
  const qualifierUserIds = useMemo(() => {
    if (!weeklyBreakdownData) return new Set<string>()
    const byDivision = new Map<string, { userId: string; best8: number }[]>()
    for (const player of weeklyBreakdownData.players) {
      if (!player.division) continue
      const group = byDivision.get(player.division) ?? []
      group.push({ userId: player.userId, best8: player.best8 })
      byDivision.set(player.division, group)
    }
    const qualifiers = new Set<string>()
    for (const players of byDivision.values()) {
      players
        .sort((a, b) => b.best8 - a.best8)
        .slice(0, 8)
        .forEach((p) => { qualifiers.add(p.userId) })
    }
    return qualifiers
  }, [weeklyBreakdownData])

  if (currentQuarter === undefined || dailyQuizzes === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (currentQuarter === null) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No current quarter is set. Please contact an administrator.
      </div>
    )
  }

  const activeDivision = selectedDivision ?? "all"
  const activeGame = selectedGame ?? "overall"

  // Derive the game name for DailyGameBreakdown from the selected quiz's name field
  const activeQuizName =
    activeGame !== "overall"
      ? (dailyQuizzes.find((q) => q._id === activeGame)?.name ?? "")
      : ""

  return (
    <div className="space-y-6">
      {/* Division Tabs */}
      <Tabs value={activeDivision} onValueChange={handleDivisionChange}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="all">All Divisions</TabsTrigger>
          {ALL_DIVISIONS.map((div) => (
            <TabsTrigger key={div} value={div}>
              {div}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* All Divisions Tab */}
        <TabsContent value="all" className="mt-6">
          <div className="space-y-4">
            <WeekDayPicker
              quizzes={dailyQuizzes}
              activeGame={activeGame}
              onGameChange={handleGameChange}
            />
            {activeGame === "overall" ? (
              <WeeklyBreakdownLeaderboard
                data={weeklyBreakdownData}
                title="All Divisions"
                showDivision
              />
            ) : (
              <DailyGameBreakdown
                data={gameBreakdown}
                gameName={activeQuizName}
                showDivision
                qualifierUserIds={qualifierUserIds}
              />
            )}
          </div>
        </TabsContent>

        {/* Division-specific Tabs */}
        {ALL_DIVISIONS.map((div) => (
          <TabsContent key={div} value={div} className="mt-6">
            {selectedDivision === div ? (
              <div className="space-y-4">
                <WeekDayPicker
                  quizzes={dailyQuizzes}
                  activeGame={activeGame}
                  onGameChange={handleGameChange}
                />
                {activeGame === "overall" ? (
                  <WeeklyBreakdownLeaderboard
                    data={weeklyBreakdownData}
                    title={`Division ${div}`}
                    showDivision={false}
                  />
                ) : (
                  <DailyGameBreakdown
                    data={gameBreakdown}
                    gameName={activeQuizName}
                    showDivision={false}
                    qualifierUserIds={qualifierUserIds}
                  />
                )}
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}